import axios from 'axios';
import React, { Fragment, useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addItem, increaseQuantity, decreaseQuantity } from '../redux/cart';
import { FaStar, FaRegStar, FaStarHalfAlt, FaSearch, FaTimes } from 'react-icons/fa';
import { ToastContainer, Zoom, toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;

function SearchPage() {
    const [search, setSearch] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const dispatch = useDispatch();
    const cartItems = useSelector(state => state.cart.items || []);
    const debounceRef = useRef(null);
    const cancelRef = useRef(null);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(-1); // for keyboard navigation
    const [showSuggestions, setShowSuggestions] = useState(false);

    const fetchProducts = async (query) => {
        if (cancelRef.current) cancelRef.current.cancel();
        cancelRef.current = axios.CancelToken.source();
        try {
            const response = await axios.get(`${API_BASE_URL}/product-list?search=${encodeURIComponent(query)}`, { cancelToken: cancelRef.current.token });
            const data = Array.isArray(response.data) ? response.data : [];
            const namesSeen = new Set();
            const uniq = [];
            for (const p of data) {
                if (!p?.name) continue;
                const n = p.name;
                if (!namesSeen.has(n)) { namesSeen.add(n); uniq.push(p); }
                if (uniq.length >= 8) break;
            }
            setSuggestions(uniq);
            setProducts(data);
            setShowSuggestions(true);
        } catch (err) {
            if (!axios.isCancel(err)) {
                console.error('Error fetching search results:', err);
                setError('Failed to fetch results');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase();
        setSearch(query);
        setError('');
        setActiveIndex(-1);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (query.length < 3) {
            setSuggestions([]);
            setProducts([]);
            setShowSuggestions(false);
            // cancel any pending request
            if (cancelRef.current) cancelRef.current.cancel();
            return;
        }

    setLoading(true);
    debounceRef.current = setTimeout(() => fetchProducts(query), 400);
    };

    const handleSuggestionClick = (name) => {
        const lowered = name.toLowerCase();
        setSearch(lowered);
        setSuggestions([]); // clear while loading fresh data
        setShowSuggestions(false);
        setActiveIndex(-1);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setLoading(true);
        fetchProducts(lowered).then(()=>{
            setShowSuggestions(false); // keep suggestions hidden after selection
        });
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            if (e.key === 'Escape') {
                setShowSuggestions(false);
                setActiveIndex(-1);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter') {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
                e.preventDefault();
                handleSuggestionClick(suggestions[activeIndex].name);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setActiveIndex(-1);
        }
    };

    // close suggestions on outside click
    useEffect(() => {
        const outside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
                setActiveIndex(-1);
            }
        };
        document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, []);

    const clearSearch = () => {
        setSearch('');
        setSuggestions([]);
        setProducts([]);
        setActiveIndex(-1);
        setShowSuggestions(false);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (cancelRef.current) cancelRef.current.cancel();
        inputRef.current?.focus();
    };

    const toastConfig = {
        autoClose: 500,
        hideProgressBar: true,
        transition: Zoom,
        icon: false,
        closeButton: false,
        style: {
            width: '420px',
            border: '1px solid #bbbbbb',
            boxShadow: '0 0 21px #ed3237',
            textAlign: 'center',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ed3237',
            fontSize: '16px'
        }
    };

    const handleAddToCart = useCallback((product) => {
        dispatch(addItem(product));
        toast.success('Item added to cart', toastConfig);
    }, [dispatch]);

    const handleInc = useCallback((id) => {
        dispatch(increaseQuantity(id));
    }, [dispatch]);

    const handleDec = useCallback((id) => {
        dispatch(decreaseQuantity(id));
    }, [dispatch]);

    // cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (cancelRef.current) cancelRef.current.cancel();
        };
    }, []);

    const highlight = (name) => {
        if (!search) return name;
        const idx = name.toLowerCase().indexOf(search);
        if (idx === -1) return name.toUpperCase();
        const before = name.slice(0, idx).toUpperCase();
        const mid = name.slice(idx, idx + search.length).toUpperCase();
        const after = name.slice(idx + search.length).toUpperCase();
        return <span>{before}<mark style={{background:'#fff2f2', color:'#c40000', padding:0}}>{mid}</mark>{after}</span>;
    };

    return (
        <Fragment>
            <div className="container-fluid g-0 p-0 overflow-hidden m-t25">
                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12 col-12 position-relative" ref={wrapperRef}>
                        <div className='search-shell shadow-sm'>
                            <FaSearch className='search-shell-icon' aria-hidden='true' />
                            <input
                                ref={inputRef}
                                className="search-shell-input"
                                type="text"
                                placeholder="Search for products..."
                                value={search}
                                onChange={handleSearch}
                                onFocus={()=>{ if (suggestions.length>0) setShowSuggestions(true); }}
                                onKeyDown={handleKeyDown}
                                aria-autocomplete='list'
                                aria-expanded={showSuggestions}
                                role='combobox'
                            />
                            {search && (
                                <button type='button' className='search-clear-btn' aria-label='Clear search' onClick={clearSearch}><FaTimes /></button>
                            )}
                            {loading && (
                                <span className='spinner-border spinner-border-sm text-danger position-absolute' style={{top:'50%',right:'44px',transform:'translateY(-50%)'}} />
                            )}
                        </div>
                        {showSuggestions && suggestions.length > 0 && (
                            <ul className='suggestions-panel' role='listbox'>
                                {suggestions.map((sug, i) => (
                                    <li
                                        key={sug._id || sug.name}
                                        role='option'
                                        aria-selected={i===activeIndex}
                                        className={'suggestion-row'+(i===activeIndex?' active':'')}
                                        onMouseEnter={()=>setActiveIndex(i)}
                                        onMouseLeave={()=>setActiveIndex(-1)}
                                        onClick={()=>handleSuggestionClick(sug.name)}
                                    >
                                        <div className='sug-img-wrap'>
                                            {sug.image1 ? <img src={`${API_BASE_URL}/${sug.image1}`} alt='' loading='lazy' /> : <div className='img-fallback'>?</div>}
                                        </div>
                                        <div className='sug-meta'>
                                            <div className='sug-name'>{highlight(sug.name)}</div>
                                            <div className='sug-price'>₹{sug.price}{sug.unit?`/${sug.unit}`:''}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {search.length >= 3 && !loading && suggestions.length === 0 && !error && (
                            <div className='suggestions-panel empty small text-muted'>No quick suggestions</div>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="text-center py-2">
                        <span className="spinner-border text-danger" role="status"></span>
                    </div>
                )}

                {search.length >= 3 && !loading && products.length > 0 && (
                    <div className="container-fluid">
                        <div className="row">
                            {products.map((item, idx) => {
                                const original = Number(item.originalPrice || item.price || 0);
                                const price = Number(item.price || 0);
                                const hasDiscount = original > price && original > 0;
                                const discountPct = hasDiscount ? Math.round(((original - price) / original) * 100) : 0;
                                const ratingVal = Number(item.rating || 0);
                                const stars = [];
                                for (let i = 1; i <= 5; i++) {
                                    if (ratingVal >= i) stars.push(<FaStar key={i} className='text-warning' />); else if (ratingVal >= i - 0.5) stars.push(<FaStarHalfAlt key={i} className='text-warning' />); else stars.push(<FaRegStar key={i} className='text-warning' />);
                                }
                                const cartEntry = cartItems.find(ci => ci._id === item._id || ci.name === item.name);
                                const inCart = !!cartEntry;
                                return (
                                    <div className="col-lg-3 col-md-3 col-sm-4 col-6 prod-list border text-center position-relative" key={item._id || idx}>
                                        <Link to={`/product/${item.name}`} className="d-block w-100 position-relative" aria-label={`View product ${item.name}`}>
                                            {hasDiscount && (
                                                <span className='discount-badge' aria-label={`${discountPct}% discount`}>{discountPct}%</span>
                                            )}
                                            <img src={`${API_BASE_URL}/${item.image1}`} alt={item.name} className="w-100" />
                                            <label>{item.name.toUpperCase()}</label>
                                        </Link>
                                        <div className='d-flex justify-content-center align-items-center gap-1 mb-1' style={{ minHeight: '20px' }}>
                                            {stars}
                                        </div>
                                        <div className='d-flex justify-content-between align-items-center mb-1 w-100' style={{fontSize:'13px',textAlign:'left'}}>
                                            <div className='d-flex align-items-center flex-wrap'>
                                                <strong className='text-danger me-1' style={{fontSize:'15px'}}>₹{price}</strong>
                                                {hasDiscount && <span className='text-muted text-decoration-line-through' style={{ fontSize: '12px' }}>₹{original}</span>}
                                            </div>
                                            {item.quantity && item.unit ? (
                                                <span className='text-muted small'>{item.quantity}{item.unit}</span>
                                            ) : <span className='invisible small'>.</span>}
                                        </div>
                                        {!inCart ? (
                                            <button onClick={() => handleAddToCart(item)}>ADD TO CART</button>
                                        ) : (
                                            <div className='qty-bar'>
                                                <button type='button' aria-label='Decrease quantity' onClick={() => handleDec(cartEntry._id)}>-</button>
                                                <span className='qty-mid'>{cartEntry.quantity}</span>
                                                <button type='button' aria-label='Increase quantity' onClick={() => handleInc(cartEntry._id)}>+</button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {search.length >= 3 && !loading && products.length === 0 && (
                    <div className='text-center py-4 text-muted' style={{fontSize:'13px'}}>No products found for "{search}"</div>
                )}
            </div>
            <div className="mt-5 pt-5 mb-5"></div>
            <ToastContainer />
        </Fragment>
    );
}

export default SearchPage;
